import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { numberIsGreaterThan } from 'components/forms/inputs/rhf-validators/validators';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { JobRoleForm } from 'types/jobRoles';
import { t } from 'utils/getTranslations';

export const JOB_ROLE_FORM_CLASSIFIERS = ['JOB_ROLE_GROUP_TYPE', 'LANGUAGE'];

const defaultValues: JobRoleForm = {
    name: '',
    internalName: '',
    abbreviation: '',
    orderNum: null,
    groupNameType: null,
    isActive: true,
    excludeFromMinmaxCalculation: false,
    entityTexts: [],
    jobRoleEmployees: [],
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

/**
 * The job role form's state, shared by the create and edit pages and the cards; the form takes the custom fields'
 * values from the fields it is given.
 */
export const useJobRoleForm = ({
    initialData,
    classifiers,
    entityCustomFields = EMPTY_CUSTOM_FIELDS,
}: {
    initialData?: JobRoleForm;
    classifiers: Record<string, ClassifierData>;
    entityCustomFields?: CustomField[];
}) => {
    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType: EntityType.JOB_ROLE, fields: entityCustomFields });

    const methods = useForm<JobRoleForm>({
        defaultValues: { ...defaultValues, ...initialData, ...initialCustomFieldsObject },
        values: initialData ? { ...initialData, ...initialCustomFieldsObject } : undefined,
    });

    // Validate if order number is greater than 0. Backend errors on 0. An empty one is left to the required rule,
    // which a draft save turns off.
    const greaterThanZero = useCallback(
        (val: unknown) =>
            val === null || val === undefined || val === ''
                ? true
                : numberIsGreaterThan(0, t('validation.must-be-greater-than', { greaterThan: '0' }))(val),
        []
    );

    return {
        methods,
        classifiers,
        greaterThanZero,
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoadingCustomFieldData,
    };
};

export type JobRoleFormState = ReturnType<typeof useJobRoleForm>;
